import { makeAutoObservable, runInAction } from 'mobx';
import axios from 'axios';
import synthesiaAgent from '../../services/api/synthesiaAgent';
import agent from '../../services/api/agent';
import SnackbarUtils from '../../components/snackbarUtils/SnackbarUtilsConfigurator';
import { store } from '../store';

export interface ISynthesiaVideoModel {
  id?: string;
  title: string;
  description: string;
  published: string;
  link: string;
  img: string;
  duration: string;
  views: string;
  user: string;
  downloadUrl?: string;
}

export default class SynthesiaStore {
  isSynthesiaOpen = false;
  loading = false;
  loadingMore = false;
  searchQuery = '';
  nextOffset: number | null = 0;
  uploading = false;

  hasIntegration: boolean | null = null;

  synthesiaOwnerId: number | null = null;

  allVideos: ISynthesiaVideoModel[] = [];

  private defaultThumbnail = '/img/defaultThumbnailVideo.png';

  constructor() {
    makeAutoObservable(this);
  }
  openSynthesia = () => {
    this.isSynthesiaOpen = true;
    if (this.allVideos.length === 0) {
      this.hasIntegration = null;
      void this.loadVideos();
    }
  };
  closeSynthesia = () => {
    this.isSynthesiaOpen = false;
    this.setLoading(false);
    this.loadingMore = false;
    this.setSearchQuery('');
    this.hasIntegration = null;
  };
  setLoading = (state: boolean) => {
    this.loading = state;
  };

  setSearchQuery = (query: string) => {
    this.searchQuery = query;
  };

  setOwnerId = (ownerId: number | null) => {
    this.synthesiaOwnerId = typeof ownerId === 'number' && !isNaN(ownerId) ? ownerId : null;
  };

  get filteredVideoList(): ISynthesiaVideoModel[] {
    const q = this.searchQuery.trim().toLowerCase();
    if (!q) return this.allVideos;
    return this.allVideos.filter((v) => v.title.toLowerCase().includes(q) || v.description.toLowerCase().includes(q));
  }

  private formatDuration(totalSeconds?: number): string {
    if (!totalSeconds || totalSeconds <= 0) return '';
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);
    const mm = minutes < 10 && hours > 0 ? `0${minutes}` : `${minutes}`;
    const ss = seconds < 10 ? `0${seconds}` : `${seconds}`;
    return hours > 0 ? `${hours}:${mm}:${ss}` : `${minutes}:${ss}`;
  }

  private resolveDateFromUnknown(input: any): Date | null {
    if (!input && input !== 0) return null;
    if (typeof input === 'number') {
      const ms = input < 10_000_000_000 ? input * 1000 : input;
      return new Date(ms);
    }
    const asString = input.toString();
    const parsed = Number(asString);
    if (!isNaN(parsed)) {
      const ms = parsed < 10_000_000_000 ? parsed * 1000 : parsed;
      return new Date(ms);
    }
    // last resort
    const d = new Date(asString);
    return isNaN(d.getTime()) ? null : d;
  }

  private normalizeDurationFromAny(video: any): string {
    if (typeof video?.duration === 'string' && video.duration.length > 0) {
      return video.duration.split('.')[0];
    }
    const durationSeconds: number | undefined = Number(video?.durationInSeconds || video?.duration_seconds || video?.duration) || undefined;
    return this.formatDuration(durationSeconds);
  }

  private mapSynthesiaVideoToUIModel = (video: any): ISynthesiaVideoModel | null => {
    const status = (video?.status || '').toString().toLowerCase();
    if (status && !(status === 'completed' || status === 'complete')) return null;

    const title: string = (video?.title || video?.name || 'Untitled').toString();

    const date = this.resolveDateFromUnknown(video?.updatedAt ?? video?.createdAt ?? video?.updated_at ?? video?.created_at ?? null);
    const day = date ? (date.getDate() < 10 ? '0' + date.getDate() : '' + date.getDate()) : '';
    const monthIndex = date ? date.getMonth() + 1 : 0;
    const month = date ? (monthIndex < 10 ? '0' + monthIndex : '' + monthIndex) : '';
    const published = date ? `${day}.${month}.${date.getFullYear()}` : '';

    const duration = this.normalizeDurationFromAny(video);

    const mp4Url: string = typeof video?.download === 'string' ? video.download : '';

    const thumbnailUrl: string = typeof video?.thumbnail === 'string' ? video.thumbnail : video?.thumbnail?.image || '';

    return {
      id: (video?.id || '').toString() || undefined,
      title,
      description: '',
      published,
      link: mp4Url || '',
      img: thumbnailUrl || this.defaultThumbnail,
      duration: duration,
      views: '',
      user: '',
      downloadUrl: mp4Url || undefined,
    };
  };

  // we could also implement the proxy on backend for this request to avoid exposing the API key
  loadVideos = async () => {
    if (this.synthesiaOwnerId == null) return;

    this.setLoading(true);
    try {
      const limit = 20;
      const offset = 0;
      const json: any = await synthesiaAgent.videos.list(this.synthesiaOwnerId, undefined, limit, offset);

      const rawList: any[] = json?.videos || json?.items || json?.data?.videos || json?.data?.items || json?.results || [];

      const mapped: ISynthesiaVideoModel[] = [];
      rawList.forEach((v: any) => {
        const mappedVideo = this.mapSynthesiaVideoToUIModel(v);
        if (mappedVideo) mapped.push(mappedVideo);
      });

      runInAction(() => {
        this.allVideos = mapped;
        this.nextOffset = mapped.length < limit ? null : offset + limit;
        this.hasIntegration = true;
      });
    } catch (error) {
      console.error('Failed to load Synthesia videos', error);
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        runInAction(() => {
          this.hasIntegration = false;
        });
      }
    } finally {
      this.setLoading(false);
    }
  };

  loadMore = async () => {
    if (this.synthesiaOwnerId == null) return;
    if (this.nextOffset == null) return;
    if (this.loadingMore) return;

    this.loadingMore = true;
    try {
      const limit = 20;
      const currentOffset = this.nextOffset;
      const json: any = await synthesiaAgent.videos.list(this.synthesiaOwnerId, undefined, limit, currentOffset);
      const rawList: any[] = json?.videos || json?.items || json?.data?.videos || json?.data?.items || json?.results || [];
      const mapped: ISynthesiaVideoModel[] = [];
      rawList.forEach((v: any) => {
        const mappedVideo = this.mapSynthesiaVideoToUIModel(v);
        if (mappedVideo) mapped.push(mappedVideo);
      });

      runInAction(() => {
        this.allVideos = [...this.allVideos, ...mapped];
        this.nextOffset = mapped.length < limit || currentOffset == null ? null : currentOffset + limit;
      });
    } catch (error) {
      console.error('Failed to load more Synthesia videos', error);
    } finally {
      this.loadingMore = false;
    }
  };

  private sanitizeFilename(input: string, fallback: string, extension: string): string {
    const base = (input || fallback)
      .toString()
      .trim()
      .replace(/[\\/:*?"<>|\n\r]+/g, ' ')
      .replace(/\s+/g, ' ')
      .substring(0, 80)
      .trim();
    const name = base.length > 0 ? base : fallback;
    const ext = (extension.startsWith('.') ? extension.slice(1) : extension).toLowerCase();
    const sanitizedName = name.replace(/[.\s]+/g, '_').replace(/^_+|_+$/g, '');
    return `${sanitizedName}.${ext}`;
  }

  uploadVideoToMediaLibrary = async (video: ISynthesiaVideoModel, _folderPath?: string) => {
    if (this.uploading) return;
    const url = video.downloadUrl || video.link;
    if (!url) {
      SnackbarUtils.warning('No download URL found for this video.');
      return;
    }
    this.uploading = true;
    try {
      SnackbarUtils.info('Downloading the video file into your media library...');
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to download video');
      const blob = await response.blob();

      const safeName = this.sanitizeFilename(video.title || 'Synthesia_Video', 'Synthesia_Video', 'mp4');
      const file = new File([blob], safeName, { type: 'video/mp4' });

      const formData = new FormData();
      formData.append('formFiles', file);

      const targetFolder = 'Synthesia';
      SnackbarUtils.info('Uploading video to Media Library...');
      const res = await agent.Media.uploadFile(targetFolder, formData);

      const keys = Object.keys(res || {});
      if (keys.length > 0 && res[keys[0]]?.status === true) {
        const uploadedUrl: string = res[keys[0]].url;
        await store.pageStore.createFileEmbed(uploadedUrl, '.mp4');
        this.closeSynthesia();
      }

      if (store.mediaLibraryStore.currentFolderPath && store.mediaLibraryStore.currentFolderPath.length > 0) {
        store.mediaLibraryStore.getFilesInFolder(store.mediaLibraryStore.currentFolderPath);
      }
      SnackbarUtils.success('Video uploaded and added to page.');
    } catch (e) {
      console.error(e);
      SnackbarUtils.error('Failed to upload video.');
    } finally {
      this.uploading = false;
    }
  };
}
